'use client'

import { X } from 'lucide-react'
import React, { useEffect, useId, useRef } from 'react'
import { createPortal } from 'react-dom'

type VideoModalProps = {
  closeLabel: string
  onClose: () => void
  open: boolean
  title: string
  videoMimeType?: string
  videoUrl: string
}

export function VideoModal({
  closeLabel,
  onClose,
  open,
  title,
  videoMimeType,
  videoUrl,
}: VideoModalProps) {
  const titleId = useId()
  const closeButtonRef = useRef<HTMLButtonElement>(null)

  useEffect(() => {
    if (!open) return

    const previousOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    closeButtonRef.current?.focus()

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose()
    }

    window.addEventListener('keydown', handleKeyDown)

    return () => {
      document.body.style.overflow = previousOverflow
      window.removeEventListener('keydown', handleKeyDown)
    }
  }, [onClose, open])

  if (!open) return null

  return createPortal(
    <div
      aria-labelledby={titleId}
      aria-modal="true"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4"
      onClick={onClose}
      role="dialog"
    >
      <div
        className="relative w-full max-w-4xl rounded-2xl bg-bluetext p-6 shadow-xl lg:p-8"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="mb-4 flex items-start justify-between gap-4">
          <h2
            className="font-display text-2xl font-semibold leading-tight text-white md:text-3xl"
            id={titleId}
          >
            {title}
          </h2>
          <button
            ref={closeButtonRef}
            aria-label={closeLabel}
            className="inline-flex size-10 shrink-0 items-center justify-center rounded-full border border-white/20 text-white transition-colors hover:bg-white/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-turquoise/50 focus-visible:ring-offset-2 focus-visible:ring-offset-bluetext"
            onClick={onClose}
            type="button"
          >
            <X aria-hidden className="size-5" />
          </button>
        </div>

        <div className="flex aspect-video items-center justify-center overflow-hidden rounded-xl border border-white/15 bg-black/35">
          <video
            controls
            className="size-full object-contain"
            playsInline
            preload="metadata"
          >
            <source src={videoUrl} type={videoMimeType ?? undefined} />
          </video>
        </div>
      </div>
    </div>,
    document.body,
  )
}
